// =============================================================================
// MeuDriver — Booking Step 2: Pickup address + notes
// =============================================================================

import { useEffect } from 'react'
import { ChevronLeft, ChevronRight, MapPin, User, Bookmark } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useBookingStore } from '@/store/bookingStore'
import { formatCurrency, getStudentPrice } from '@/lib/utils'
import type { InstructorWithProfile } from '@/types/database'
import { useStudentAddresses } from '@/hooks/useStudentAddresses'
import { AddressAutocomplete } from './AddressAutocomplete'

interface Props {
  instructor: InstructorWithProfile
  onNext: () => void
  onBack: () => void
}

export function BookingStep2Address({ instructor, onNext, onBack }: Props) {
  const store = useBookingStore()
  const { addresses, isLoading: loadingAddresses } = useStudentAddresses()

  // Preço ao aluno (instrutor + 40 %) — esta tela só exibe valores.
  const hourlyRate = getStudentPrice(instructor.hourly_rate ?? 0)
  // O total já inclui a taxa da plataforma (hourlyRate acima é o preço ao aluno;
  // sai do repasse ao instrutor, não é somada ao aluno). Descontos de cupom/
  // vale-aula entram só no passo 3.
  const subtotal = hourlyRate * (store.durationMinutes / 60)
  const total = subtotal

  const canProceed = store.pickupAddress.trim().length >= 5

  // Auto-preenche com o endereço padrão na primeira render, se o store
  // estiver vazio. Não sobrescreve se o usuário já digitou algo.
  useEffect(() => {
    if (store.pickupAddress.trim() === '' && !loadingAddresses) {
      const def = addresses.find((a) => a.is_default)
      if (def) {
        store.setPickup(def.address, def.lat ?? undefined, def.lng ?? undefined)
      }
    }
    // intencional: só roda quando addresses carrega
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAddresses])

  function handleSavedAddressPick(id: string) {
    const found = addresses.find((a) => a.id === id)
    if (found) {
      store.setPickup(found.address, found.lat ?? undefined, found.lng ?? undefined)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="pickup-address" className="text-base font-semibold flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          Endereço de partida
        </Label>

        {addresses.length > 0 && (
          <div className="flex items-center gap-2 mb-1">
            <Bookmark className="h-3.5 w-3.5 text-primary shrink-0" />
            <Select onValueChange={handleSavedAddressPick}>
              <SelectTrigger className="text-sm h-9">
                <SelectValue placeholder="Usar um endereço salvo..." />
              </SelectTrigger>
              <SelectContent>
                {addresses.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="font-medium">{a.label}</span>
                    <span className="text-muted-foreground"> — {a.address}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <AddressAutocomplete
          value={store.pickupAddress}
          onChange={(val, geocoded) => {
            store.setPickup(val, geocoded?.lat, geocoded?.lon)
          }}
          placeholder="Rua, número, bairro, cidade"
        />
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <User className="h-3.5 w-3.5" />
          O instrutor irá até você
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes" className="text-base font-semibold">
          Observações (opcional)
        </Label>
        <Textarea
          id="notes"
          placeholder="Exemplo: Tenho dificuldade com baliza, prefiro começar por ruas mais tranquilas..."
          rows={3}
          maxLength={500}
          value={store.notes}
          onChange={(e) => store.setNotes(e.target.value)}
        />
        <p className="text-xs text-muted-foreground text-right">
          {store.notes.length}/500
        </p>
      </div>

      <Card className="bg-gray-50">
        <CardHeader className="pb-3 pt-4 px-5">
          <CardTitle className="text-base">Resumo do valor</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {formatCurrency(hourlyRate)}/h × {store.durationMinutes / 60}h
            </span>
            <span className="font-medium">{formatCurrency(subtotal)}</span>
          </div>
          <div className="border-t pt-2 mt-2 flex justify-between font-semibold text-base">
            <span>Total</span>
            <span className="text-primary">{formatCurrency(total)}</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" />
          Voltar
        </Button>
        <Button
          size="lg"
          disabled={!canProceed}
          onClick={onNext}
          className="flex-1 sm:flex-none gap-1"
        >
          Próximo
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
